/**
 * ShortcutsSheet — keyboard shortcuts (Sep 2026). `?` opens a sheet listing
 * every shortcut; `g` then a letter jumps to a place (GitHub/Linear style).
 * Mounted once in Layout next to the ⌘K palette. Keys are ignored while the
 * user is typing in a field, and when a modifier is held.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from './Modal';
import { isMac } from './CommandPalette';
import { markUsed } from '../lib/usage';

const OPEN_EVENT = 'daaily-open-shortcuts';
export const openShortcutsSheet = () => window.dispatchEvent(new Event(OPEN_EVENT));

/** `g` + key → route. Keep in step with the GOTO rows in the sheet. */
const GOTO: Record<string, { to: string; label: string }> = {
  h: { to: '/', label: 'Home' },
  a: { to: '/activity', label: 'Activity (all to-dos)' },
  d: { to: '/drivers', label: 'DAAily Drivers' },
  n: { to: '/drivers/new', label: 'New Drivers nomination' },
  u: { to: '/guide', label: 'Guide' },
  p: { to: '/profile', label: 'Your profile' },
};

function typingIn(el: EventTarget | null): boolean {
  const t = el as HTMLElement | null;
  if (!t) return false;
  return t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName);
}

function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k, i) => (
        <kbd key={i} className="min-w-[1.5rem] h-6 px-1.5 inline-grid place-items-center rounded border border-line-strong bg-surface-2 text-xs font-sans text-fg">{k}</kbd>
      ))}
    </span>
  );
}

export function ShortcutsSheet() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const gAt = useRef(0);

  useEffect(() => {
    const onOpen = () => { setOpen(true); markUsed('shortcuts'); };
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typingIn(e.target)) return;
      if (document.querySelector('[role="dialog"]') && e.key !== '?') return; // a modal/palette owns the keyboard
      if (e.key === '?') {
        e.preventDefault();
        setOpen((o) => !o);
        markUsed('shortcuts');
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'g' && !e.shiftKey) { gAt.current = Date.now(); return; }
      if (gAt.current && Date.now() - gAt.current < 1200 && GOTO[k]) {
        e.preventDefault();
        gAt.current = 0;
        markUsed('goto');
        navigate(GOTO[k]!.to);
        return;
      }
      gAt.current = 0;
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(OPEN_EVENT, onOpen);
      window.removeEventListener('keydown', onKey);
    };
  }, [navigate]);

  const mod = isMac ? '⌘' : 'Ctrl';
  const groups: { title: string; rows: { keys: string[]; label: string }[] }[] = [
    {
      title: 'Anywhere',
      rows: [
        { keys: [mod, 'K'], label: 'Search apps, pages, to-dos and people' },
        { keys: ['?'], label: 'Show or hide this sheet' },
        { keys: ['Esc'], label: 'Close a dialog, drawer or the search' },
      ],
    },
    { title: 'Go to', rows: Object.entries(GOTO).map(([k, v]) => ({ keys: ['g', k], label: v.label })) },
    {
      title: 'In search',
      rows: [
        { keys: ['↑', '↓'], label: 'Move through results' },
        { keys: ['Enter'], label: 'Open the selected result' },
      ],
    },
  ];

  return (
    <Modal open={open} onClose={() => setOpen(false)} title="Keyboard shortcuts" widthClass="max-w-md">
      <div className="space-y-5">
        {groups.map((g) => (
          <section key={g.title}>
            <h3 className="text-2xs font-semibold uppercase text-fg-faint">{g.title}</h3>
            <ul className="mt-2 divide-y divide-line">
              {g.rows.map((r) => (
                <li key={r.label} className="flex items-center justify-between gap-4 py-2 text-sm text-fg">
                  <span>{r.label}</span>
                  <Keys keys={r.keys} />
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="text-xs text-fg-muted">For &ldquo;Go to&rdquo;, press g, let go, then the letter.</p>
      </div>
    </Modal>
  );
}
